import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@flint/api/router";
import { useNavigate } from "@tanstack/react-router";
import { Skeleton, toast } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { toDateInput } from "./collection-format.ts";
import {
  CollectionForm,
  toCollectionPayload,
  type CollectionFormValues,
} from "./collection-form.tsx";

/**
 * Store admin — Edit collection (Commerce, M6).
 *
 * Details only: membership is curated on Detail, and Status is a read-only
 * badge because the phase is derived from the season window.
 *
 * @see prototype/store/admin/all.html#collections-edit
 */
export function CollectionEditPage({ collectionId }: { collectionId: string }) {
  const q = trpc.storeCollections.get.useQuery({ collectionId });
  const c = q.data;

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Collections", to: "/store/admin/collections" },
        { label: c?.name ?? "…", to: "/store/admin/collections" },
        { label: "Edit" },
      ]}
    >
      {c ? (
        // Keyed on the row so every field — the Radix `Select` included —
        // initialises from saved data rather than mounting empty and resetting.
        <EditForm key={c.collectionId} collection={c} />
      ) : (
        <StorePageColumn>
          <div className="flex items-center gap-3">
            <BackButton fallbackTo="/store/admin/collections" label="Back to collections" />
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Edit collection
            </h1>
          </div>
          {q.isLoading ? (
            <Skeleton className="mt-5 h-40 w-full" />
          ) : (
            <p className="mt-5 text-sm text-muted">Collection not found.</p>
          )}
        </StorePageColumn>
      )}
    </StoreAdminLayout>
  );
}

type Collection = inferRouterOutputs<AppRouter>["storeCollections"]["get"];

function EditForm({ collection: c }: { collection: Collection }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const update = trpc.storeCollections.update.useMutation();
  const backToDetail = () =>
    void navigate({
      to: "/store/admin/collections/$collectionId",
      params: { collectionId: c.collectionId },
    });

  async function onSubmit(v: CollectionFormValues) {
    try {
      await update.mutateAsync({ collectionId: c.collectionId, ...toCollectionPayload(v) });
      await Promise.all([
        utils.storeCollections.get.invalidate({ collectionId: c.collectionId }),
        utils.storeCollections.list.invalidate(),
      ]);
      toast.success("Changes saved");
      backToDetail();
    } catch (err) {
      toast.error("Couldn't save changes", { description: storeErrorMessage(err) });
    }
  }

  return (
    <StorePageColumn>
      <div className="flex items-center gap-3">
        <BackButton
          fallbackTo={`/store/admin/collections/${c.collectionId}`}
          label="Back to collection"
        />
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
            Edit collection
          </h1>
          <p className="mt-1.5 text-sm text-muted">{c.name}</p>
        </div>
      </div>

      <CollectionForm
        defaultValues={{
          name: c.name,
          type: c.type,
          activeFrom: toDateInput(c.activeFrom),
          activeTo: toDateInput(c.activeTo),
          description: c.description ?? "",
          imageUrl: c.imageUrl ?? "",
        }}
        phase={c.phase}
        lockType
        busy={update.isPending}
        submitLabel="Save changes"
        onSubmit={onSubmit}
        onCancel={backToDetail}
      />
    </StorePageColumn>
  );
}
